import { Head, useForm } from '@inertiajs/react'
import { useState, type FormEvent } from 'react'
import { Button, Switch } from 'antd'
import ApiTokensManager, { type ApiToken, type NewToken } from '../components/Settings/ApiTokensManager'
import AiKeysManager from '../components/Settings/AiKeysManager'
import type { AiProps } from '../types/ai'
import { testNotification, getNotificationPermission } from '../utils/notificationService'
import { userSettingsStorage, isLocalStorageAvailable } from '../utils/storage/localStorage'

interface Settings {
  id: number
  work_duration: number
  break_duration: number
  sound_notification: boolean
  web_notification: boolean
  auto_complete_task: boolean
  timer_max_hours: number
  daily_task_cap: number
  user_id: number
}

interface SettingsProps {
  settings: Settings
  api_tokens: ApiToken[]
  new_api_token: NewToken | null
  ai: AiProps
}

export default function SettingsPage({ settings, api_tokens, new_api_token, ai }: SettingsProps) {
  const form = useForm({
    work_duration: settings.work_duration ?? 25,
    break_duration: settings.break_duration ?? 5,
    sound_notification: settings.sound_notification ?? true,
    web_notification: settings.web_notification ?? true,
    auto_complete_task: settings.auto_complete_task ?? true,
    timer_max_hours: settings.timer_max_hours ?? 8,
    daily_task_cap: settings.daily_task_cap ?? 5,
  })
  const [testing, setTesting] = useState(false)
  const [testMsg, setTestMsg] = useState<string | null>(null)

  const save = (e: FormEvent) => {
    e.preventDefault()
    form.patch('/settings', {
      preserveScroll: true,
      onSuccess: () => {
        // Mirror to localStorage so the timer on / picks up the new settings.
        if (isLocalStorageAvailable()) {
          userSettingsStorage.set({
            workDuration: form.data.work_duration,
            breakDuration: form.data.break_duration,
            soundNotification: form.data.sound_notification,
            webNotification: form.data.web_notification,
            autoCompleteTask: form.data.auto_complete_task,
          })
        }
      },
    })
  }

  const handleTestNotification = async () => {
    if (!form.data.web_notification) {
      setTestMsg('请先启用网页通知')
      return
    }
    setTesting(true)
    try {
      const ok = await testNotification(true)
      if (ok) {
        setTestMsg('通知测试成功！如果您没有看到通知，请检查系统设置。')
      } else {
        setTestMsg(getNotificationPermission() === 'denied' ? '通知被浏览器或系统阻止。请在浏览器设置中允许通知。' : '发送通知失败，请检查浏览器设置。')
      }
    } finally {
      setTesting(false)
      setTimeout(() => setTestMsg(null), 5000)
    }
  }

  return (
    <div className="py-12 px-4 sm:px-6 lg:px-8">
      <Head title="设置" />
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-center text-gray-900 mb-8">设置</h1>

        {testMsg && <div className="mb-4 p-4 bg-blue-50 text-blue-700 rounded-md">{testMsg}</div>}

        <form onSubmit={save} className="bg-white shadow-sm rounded-lg p-6">
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-medium text-gray-900">番茄钟设置</h2>
              <div className="mt-4 space-y-4">
                <div>
                  <label htmlFor="work_duration" className="block text-sm font-medium text-gray-700">工作时长（分钟）</label>
                  <input
                    type="number"
                    id="work_duration"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    value={form.data.work_duration}
                    onChange={(e) => form.setData('work_duration', parseInt(e.target.value, 10) || 25)}
                    min={1}
                    max={60}
                  />
                </div>
                <div>
                  <label htmlFor="break_duration" className="block text-sm font-medium text-gray-700">休息时长（分钟）</label>
                  <input
                    type="number"
                    id="break_duration"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    value={form.data.break_duration}
                    onChange={(e) => form.setData('break_duration', parseInt(e.target.value, 10) || 5)}
                    min={1}
                    max={30}
                  />
                </div>

                <div className="mt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">通知设置</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-gray-700">声音通知</span>
                        <p className="text-sm text-gray-500">在专注或休息结束时播放提示音</p>
                      </div>
                      <Switch checked={form.data.sound_notification} onChange={(c) => form.setData('sound_notification', c)} />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-gray-700">浏览器通知</span>
                        <p className="text-sm text-gray-500">在专注或休息结束时显示浏览器通知</p>
                      </div>
                      <Switch checked={form.data.web_notification} onChange={(c) => form.setData('web_notification', c)} />
                    </div>
                    {form.data.web_notification && (
                      <Button onClick={handleTestNotification} disabled={testing} type="default" size="middle" loading={testing}>
                        测试通知
                      </Button>
                    )}
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-gray-700">自动完成任务</span>
                        <p className="text-sm text-gray-500">专注结束后自动将当前任务标记为完成</p>
                      </div>
                      <Switch checked={form.data.auto_complete_task} onChange={(c) => form.setData('auto_complete_task', c)} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-medium text-gray-900">自由计时设置</h2>
              <div className="mt-4 space-y-4">
                <div>
                  <label htmlFor="timer_max_hours" className="block text-sm font-medium text-gray-700">
                    忘关保护阈值（小时）
                  </label>
                  <p className="text-sm text-gray-500">计时超过这么多小时没手动停止，会被自动截停并标记为可疑</p>
                  <input
                    type="number"
                    id="timer_max_hours"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    value={form.data.timer_max_hours}
                    onChange={(e) => form.setData('timer_max_hours', parseInt(e.target.value, 10) || 8)}
                    min={1}
                    max={24}
                  />
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-medium text-gray-900">任务设置</h2>
              <div className="mt-4 space-y-4">
                <div>
                  <label htmlFor="daily_task_cap" className="block text-sm font-medium text-gray-700">
                    「今天」最多几条
                  </label>
                  <p className="text-sm text-gray-500">
                    超过这个数就得先把一条移出「今天」才能加新的。清单会不会通胀，全看这个数字
                  </p>
                  <input
                    type="number"
                    id="daily_task_cap"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    value={form.data.daily_task_cap}
                    onChange={(e) => form.setData('daily_task_cap', parseInt(e.target.value, 10) || 5)}
                    min={1}
                    max={20}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <Button type="primary" htmlType="submit" loading={form.processing}>
              {form.processing ? '保存中...' : '保存设置'}
            </Button>
          </div>
        </form>

        <div className="bg-white shadow-sm rounded-lg p-6 mt-6">
          <AiKeysManager ai={ai} />
        </div>

        <div className="bg-white shadow-sm rounded-lg p-6 mt-6">
          <ApiTokensManager tokens={api_tokens} newToken={new_api_token} />
        </div>
      </div>
    </div>
  )
}
